<script setup lang="ts">
import { computed, h, ref, watch } from "vue";
import {
  SwitchRoot,
  SwitchThumb,
  SelectContent,
  SelectIcon,
  SelectItem,
  SelectItemIndicator,
  SelectItemText,
  SelectPortal,
  SelectRoot,
  SelectScrollDownButton,
  SelectScrollUpButton,
  SelectTrigger,
  SelectValue,
  SelectViewport,
} from "reka-ui";

import { RsCheckbox } from "@/components";
import { useBrandStore, usePointStore, useMetadataStore, useCaseStore } from "@/stores";
import { useCaseRouteParams } from "@/hooks/use-case-route-params";

import Score from "./_Score.vue";
import CheckTitle from "./_CheckTitle.vue";
import CheckSCC125 from "./_CheckSCC125.vue";
import CheckEvidence from "./_CheckEvidence.vue";
import CheckDataValue from "./_CheckDataValue.vue";
import ScoreContributingRecord from "./_ScoreContributingRecord.vue";

import type { Metadata } from "@/models/Metadata";
import type { CheckPointData, ScorePointData } from "@/models/Point";
import type { DockviewPanelComponentProps, DockviewPanelComponentEmits } from "@/hooks/use-case-panel";

defineProps<DockviewPanelComponentProps<false>>();
defineEmits<DockviewPanelComponentEmits>();

const brandStore = useBrandStore();
const routeParams = useCaseRouteParams();

const config = computed(() => brandStore.viewerConfig.checks);

const showCaseScores = ref(true);
const showRecordScores = ref(true);
const isDataView = ref(
  config.value?.view == null ? config.value?.dataViewDefault === true : config.value.view === false,
);

const pointStore = usePointStore();

const scorepoints = computed(() => pointStore.scorePoints ?? { state: "LOADING" as const, data: [] });
const checkpoints = computed(() => pointStore.checkPoints ?? { state: "LOADING" as const, data: [] });
const checksets = computed(() => useCaseStore().checksets);

const filteredScorepoints = computed(() => {
  const fileReference = routeParams.value.casefile;
  return scorepoints.value.data.filter((a) => {
    // Hide case scores
    if (a.scope === "case" && !showCaseScores.value) return false;

    // Hide record scores
    if (a.scope === "record" && !showRecordScores.value) return false;

    // Only selected file's scores should be shown
    if (a.scope === "record" && (fileReference == null || a.appliesTo !== fileReference)) return false;

    return true;
  });
});

const filteredCheckpoints = computed(() => {
  const fileReference = routeParams.value.casefile;
  return checkpoints.value.data.filter((c) => c.check.scope !== "record" || c.appliesTo === fileReference);
});

const displayedData = computed(() => {
  const [before, after] = isDataView.value
    ? [checkpoints.value.data, filteredCheckpoints.value]
    : [scorepoints.value.data, filteredScorepoints.value];

  return { before: before.length, after: after.length };
});

const canToggleView = computed(() => config.value?.view == null);

const showVerifiedColumn = computed(() => !(config.value?.hideVerifiedColumn ?? false));

const rows = computed(() => {
  if (isDataView.value) {
    function getCheckRows(checkpoint: CheckPointData) {
      return [
        {
          type: "check-title" as const,
          checkpoint,
          component: h(CheckTitle, { isScore: false, checkpoint, checkpointReference: checkpoint.reference }),
        },
        checkpoint.check.subType === "Value"
          ? {
              type: "check-data-value" as const,
              checkpoint,
              component: h(CheckDataValue, { isScore: true, checkpoint, checkpointReference: checkpoint.reference }),
            }
          : undefined,
        !canExpandCheck(checkpoint) || openRows.value.get("check") !== getCheckRowKey(checkpoint)
          ? undefined
          : { type: "check-evidence" as const, checkpoint, component: h(CheckEvidence, { checkpoint }) },
      ];
    }

    return filteredCheckpoints.value.flatMap(getCheckRows).filter((i) => i != null);
  }

  const files = useMetadataStore().filteredFilesInCurrentFolder;
  const checkpoints = usePointStore().checkPoints?.data ?? [];

  function getCheckRows(
    checkpoint: CheckPointData,
    score: ScorePointData,
    scorePointReference: { connotation: ScorePointData["scoreConnotation"] } & Record<"positive" | "negative", number>,
  ) {
    if (openRows.value.get(score.scope) !== getScoreRowKey(score)) return [];

    return [
      {
        type: "check-title" as const,
        score,
        checkpoint,
        component: h(
          CheckTitle,
          { isScore: true, checkpoint, checkpointReference: checkpoint.reference },
          { scc125: () => h(CheckSCC125, { type: "check", ...scorePointReference }) },
        ),
      },
      checkpoint.check.subType === "Value"
        ? {
            type: "check-data-value" as const,
            score,
            checkpoint,
            component: h(CheckDataValue, { isScore: true, checkpoint, checkpointReference: checkpoint.reference }),
          }
        : undefined,
      !canExpandCheck(checkpoint) || openRows.value.get(`${score.scope}-check`) !== getCheckRowKey(checkpoint)
        ? undefined
        : { type: "check-evidence" as const, score, checkpoint, component: h(CheckEvidence, { checkpoint }) },
    ];
  }

  function getContributingRecordRows(score: ScorePointData) {
    if (score.scope === "record") return [];
    if (openRows.value.get(score.scope) !== getScoreRowKey(score)) return [];

    const totalFiles = new Set<string>();
    const result: Record<string, { file: Metadata; positive: number; negative: number }> = {};

    for (const { checkpointReference, positiveCount, negativeCount } of score.scorepointReferences) {
      const checkpoint = checkpoints.find((checkpoint) => checkpoint.reference === checkpointReference);
      if (checkpoint == null || checkpoint.check.scope !== "record") break;

      totalFiles.add(checkpoint.appliesTo);

      const file = files.find((f) => f.fileReference === checkpoint.appliesTo);
      if (file == null) break;

      result[checkpoint.appliesTo] ??= { file, positive: 0, negative: 0 };

      result[checkpoint.appliesTo].positive += positiveCount;
      result[checkpoint.appliesTo].negative += negativeCount;
    }

    const records = Object.values(result);
    const total = totalFiles.size;

    return [
      records.map(({ file, ...pointRef }) => ({
        type: "contributing-record" as const,
        score,
        file,
        component: h(ScoreContributingRecord, { ...pointRef, file, connotation: score.scoreConnotation }),
      })),
      records.length < total
        ? {
            type: "contributing-record-footer" as const,
            score,
            component: h("td", { colspan: 10000 }, `Showing ${records.length} of ${total}`),
          }
        : undefined,
    ];
  }

  const recordScoresDisplayRow = {
    hasUsed: false,
    get row() {
      if (this.hasUsed) return undefined;
      this.hasUsed = true;

      return {
        type: "display" as const,
        key: "records" as const,
        component: h("td", { colspan: 1000, class: "tw-bg-gray-100 tw-p-2" }, "Record Scores"),
      };
    },
  };

  return filteredScorepoints.value
    .flatMap((score) => {
      const references =
        checkpoints.length < 1
          ? []
          : score.scorepointReferences.flatMap(({ checkpointReference, positiveCount, negativeCount }) => {
              const checkpoint = checkpoints.find((checkpoint) => checkpoint.reference === checkpointReference);
              if (checkpoint == null || (score.scope !== "record" && checkpoint.check.scope === "record")) return [];

              return { checkpoint, positiveCount, negativeCount };
            });

      return [
        score.scope === "record" ? recordScoresDisplayRow.row : undefined,
        { type: "score" as const, score, component: h(Score, { score, showVerifiedState: false }) },
        ...references.map(({ checkpoint, negativeCount, positiveCount }) =>
          getCheckRows(checkpoint, score, {
            connotation: score.scoreConnotation,
            positive: positiveCount,
            negative: negativeCount,
          }),
        ),
        ...getContributingRecordRows(score),
      ].flat();
    })
    .filter((i) => i != null);
});

type RowTypeIdentifier = ScorePointData["scope"] | "check" | `${ScorePointData["scope"]}-check`;
type RowKeyValue = string | number | bigint;

const openRows = ref<Map<RowTypeIdentifier, RowKeyValue>>(new Map());
const scrollToRow = ref<RowKeyValue>();

watch(
  () => routeParams.value.casefile,
  () => {
    if (isDataView.value) return;

    const firstRecordScore = rows.value.findIndex((row) => row.type === "display" && row.key === "records");
    if (firstRecordScore < 0) return;

    scrollToRow.value = getRowKey(firstRecordScore);
  },
  { flush: "post" },
);

watch(
  scrollToRow,
  () => {
    if (scrollToRow.value == null) return;
    document.querySelector(`[data-key="${scrollToRow.value}"]`)?.scrollIntoView({ behavior: "smooth" });
  },
  { flush: "post" },
);

function canExpandCheck(checkpoint: CheckPointData) {
  return checkpoint.check.scope === "record" && checkpoint.datapointReference != null;
}

function getScoreRowKey(score: ScorePointData) {
  return `score-${score.reference}`;
}

function getCheckRowKey(checkpoint: CheckPointData) {
  return `check-${checkpoint.reference}`;
}

function getRowTypeIdentifier(item: (typeof rows.value)[number]) {
  let key: RowTypeIdentifier | undefined;

  if (item.type === "score") key = item.score.scope;
  else if (item.type === "check-title") key = "score" in item ? `${item.score.scope}-check` : "check";

  return key;
}

function getRowKey(index: number) {
  const row = rows.value[index];

  switch (row.type) {
    case "score":
      return getScoreRowKey(row.score);
    case "contributing-record":
      return `score-record-${row.file.fileReference}-${row.score.reference}`;
    case "contributing-record-footer":
      return `score-record-footer-${row.score.reference}`;
    case "check-title":
      return getCheckRowKey(row.checkpoint);
    case "check-data-value":
      return `check-value-${row.checkpoint.reference}`;
    case "check-evidence":
      return `check-evidence-${row.checkpoint.reference}`;
    case "display":
      return row.key;
  }
}

function isRowOpen(item: (typeof rows.value)[number], rowKey: RowKeyValue) {
  const rowId = getRowTypeIdentifier(item);
  if (rowId == null || openRows.value.get(rowId) !== rowKey) return;

  return true;
}

function toggleRow(item: (typeof rows.value)[number], rowKey: RowKeyValue) {
  if (item.type === "check-title" && !canExpandCheck(item.checkpoint)) return;

  const rowTypeId = getRowTypeIdentifier(item);
  if (rowTypeId == null) return;

  if (openRows.value.get(rowTypeId) === rowKey) openRows.value.delete(rowTypeId);
  else {
    openRows.value.set(rowTypeId, rowKey);
    scrollToRow.value = rowKey;
  }
}

async function changeCheckset(checkset: string): Promise<void> {
  await pointStore.updateCheckset(checkset);
}
</script>
<template>
  <div class="tw-flex tw-size-full tw-h-full tw-flex-col tw-bg-white">
    <div
      class="tw-flex tw-flex-wrap tw-items-center tw-gap-x-4 tw-gap-y-1 tw-bg-gray-800 tw-px-2 tw-py-1 tw-text-white empty:tw-hidden"
    >
      <SelectRoot
        v-if="checksets.length > 1"
        :model-value="pointStore.selectedCheckset"
        @update:model-value="changeCheckset"
      >
        <SelectTrigger
          class="tw-btn-md-gray tw-flex tw-min-w-0 tw-items-center"
          :aria-label="`checkset ${pointStore.selectedCheckset}`"
        >
          <SelectValue class="tw-grow tw-truncate"> Checkset: {{ pointStore.selectedCheckset }} </SelectValue>
          <SelectIcon>
            <i aria-hidden="true" class="fas fa-fw fa-chevron-down" />
          </SelectIcon>
        </SelectTrigger>
        <SelectPortal>
          <SelectContent
            :side-offset="4"
            position="popper"
            :avoid-collisions="true"
            class="tw-z-[100000] tw-overflow-hidden tw-rounded tw-border tw-border-gray-500 tw-bg-white tw-shadow-md"
          >
            <SelectScrollUpButton />
            <SelectViewport>
              <SelectItem
                v-for="checkset in checksets"
                :key="checkset"
                class="tw-flex tw-cursor-pointer tw-items-center tw-justify-between tw-gap-4 tw-px-4 tw-py-1 tw-text-gray-800 hover:tw-bg-purple-100 hover:tw-text-purple-900 focus:tw-bg-gray-100 focus:tw-text-gray-800 focus:tw-outline-none"
                :value="checkset"
              >
                <SelectItemText class="tw-text-sm">
                  {{ checkset }}
                </SelectItemText>
                <SelectItemIndicator class="tw-grid tw-w-5 tw-place-items-center">
                  <i :aria-hidden="true" class="fas fa-check fa-xs"></i>
                </SelectItemIndicator>
              </SelectItem>
            </SelectViewport>
            <SelectScrollDownButton />
          </SelectContent>
        </SelectPortal>
      </SelectRoot>
      <div
        v-if="canToggleView"
        class="tw-flex tw-items-center tw-justify-start tw-gap-1 tw-rounded-md tw-p-1 focus-within:tw-ring"
      >
        <label for="checksView" class="tw-min-w-max tw-cursor-pointer tw-font-medium">Scores View</label>
        <SwitchRoot
          id="checksView"
          v-model="isDataView"
          class="tw-relative tw-flex tw-h-4 tw-w-7 tw-cursor-pointer tw-items-center tw-rounded-full tw-border-[0.5px] tw-border-white tw-bg-purple-100 tw-p-0.5 tw-shadow-sm data-[state=checked]:tw-bg-white"
        >
          <SwitchThumb
            class="will-change-transform tw-relative tw-left-0 tw-block tw-aspect-square tw-w-1/2 tw-rounded-full tw-bg-purple-900 tw-shadow-sm tw-transition-[left] tw-duration-100 data-[state=checked]:tw-left-1/2 data-[state=checked]:tw-bg-green-700"
          />
        </SwitchRoot>
        <label for="checksView" class="tw-min-w-max tw-cursor-pointer tw-font-medium">Data View</label>
      </div>
      <div aria-hidden="true" class="tw-flex-1" />
      <template v-if="!isDataView">
        <RsCheckbox
          v-model="showCaseScores"
          checkbox-class="*:tw-accent-purple-900 [&:has(:checked)]:tw-ring-1 tw-ring-white"
        >
          Case Scores
        </RsCheckbox>
        <RsCheckbox
          v-model="showRecordScores"
          checkbox-class="*:tw-accent-purple-900 [&:has(:checked)]:tw-ring-1 tw-ring-white"
        >
          Record Scores
        </RsCheckbox>
      </template>
      <span>Showing {{ displayedData.after }} of {{ displayedData.before }}</span>
    </div>
    <div
      v-if="!isDataView && scorepoints.state !== 'SUCCESS'"
      class="tw-flex tw-size-full tw-items-center tw-justify-center tw-gap-1"
    >
      <template v-if="scorepoints.state === 'LOADING'">
        <i class="fas fa-spinner tw-animate-spin" />
        <span class="tw-leading-none">Loading Scorepoints</span>
      </template>
      <template v-else-if="scorepoints.state === 'EMPTY' && checkpoints.state === 'SUCCESS'">
        <i class="fas fa-triangle-exclamation" />
        <span class="tw-leading-none">
          There are no scorepoints for this record, it has likely been excluded due to a checks applicability criteria.
        </span>
      </template>
      <template v-else-if="scorepoints.state === 'EMPTY' && checkpoints.state === 'EMPTY'">
        <i class="fas fa-triangle-exclamation" />
        <span class="tw-leading-none">There are no scorepoints for this record.</span>
      </template>
      <template v-else-if="scorepoints.state === 'ERROR'">
        <i class="fas fa-triangle-exclamation" />
        <span class="tw-leading-none">Error loading scorepoints</span>
      </template>
    </div>
    <div
      v-else-if="isDataView && checkpoints.state !== 'SUCCESS'"
      class="tw-flex tw-size-full tw-items-center tw-justify-center tw-gap-1"
    >
      <template v-if="checkpoints.state === 'LOADING'">
        <i class="fas fa-spinner tw-animate-spin" />
        <span class="tw-leading-none">Loading Checkpoints</span>
      </template>
      <template v-else-if="checkpoints.state === 'EMPTY'">
        <i class="fas fa-triangle-exclamation" />
        <span class="tw-leading-none">No Checkpoints were found for this file.</span>
      </template>
      <template v-else-if="checkpoints.state === 'ERROR'">
        <i class="fas fa-triangle-exclamation" />
        <span class="tw-leading-none">Error loading checkpoints</span>
      </template>
    </div>
    <div v-else class="tw-w-full tw-flex-1 tw-scroll-p-20 tw-overflow-y-auto">
      <table
        class="tw-w-full tw-border-separate tw-border-spacing-0 tw-border-b-black/30 after:tw-inline-block [&>thead>tr]:-tw-top-[--item-start] [&_*:is(td:nth-child(2))]:!tw-border-l-0"
      >
        <thead class="tw-sticky tw-top-0 tw-z-[10000] tw-border-b tw-bg-gray-100">
          <tr class="*:tw-p-2 *:tw-text-center">
            <td class="tw-w-8">
              <span class="tw-sr-only">Icon</span>
            </td>
            <td>
              <span class="tw-sr-only">Title</span>
            </td>
            <td v-if="!isDataView" class="tw-w-20 tw-px-2">
              <span class="tw-text-xs">Score</span>
            </td>
            <td v-if="showVerifiedColumn" class="tw-w-20 tw-px-2">
              <span>Verified</span>
            </td>
          </tr>
        </thead>
        <tbody
          class="[&>[data-row=score]>*]:tw-border-black/30 [&>tr:not([data-row=check-evidence]):is(:hover,[data-open])]:tw-bg-[#f3ebff] [&>tr]:tw-bg-[--row-color,white] [&_td]:tw-border-t"
        >
          <tr
            v-for="(row, rowIdx) in rows"
            :key="getRowKey(rowIdx)"
            class="*:tw-px-2 *:tw-py-1"
            :data-row="row.type"
            :data-key="getRowKey(rowIdx)"
            :data-open="isRowOpen(row, getRowKey(rowIdx))"
            :class="
              row.type === 'score' || (row.type === 'check-title' && canExpandCheck(row.checkpoint))
                ? 'tw-cursor-pointer'
                : row.type === 'contributing-record' && routeParams.casefile === row.file.fileReference
                  ? '[--row-color:#f3ebff]'
                  : 'tw-cursor-default'
            "
            @click="toggleRow(row, getRowKey(rowIdx))"
          >
            <component :is="row.component" />
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>
